'use client';

/**
 * NeonCard Component
 *
 * A card with neon glow effects where the border is a saturated version
 * of the card's background color. Supports 6 accent colors.
 *
 * @example
 * ```tsx
 * <NeonCard accent="cyan">
 *   <Typography>Content with cyan glow</Typography>
 * </NeonCard>
 * ```
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { type HTMLMotionProps, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, type NeonGlowIntensity } from '../../theme/shadows';
import { interactionAnimations, motionPresets, transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface NeonCardProps extends Omit<BoxProps, 'component'> {
  /**
   * Neon accent color
   * @default 'cyan'
   */
  accent?: NeonAccent;

  /**
   * Enable glow effect on hover
   * @default true
   */
  glowOnHover?: boolean;

  /**
   * Glow intensity
   * @default 'subtle' (on hover: 'medium')
   */
  glowIntensity?: NeonGlowIntensity;

  /**
   * Enable hover lift animation
   * @default true
   */
  hoverLift?: boolean;

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Custom border radius
   * @default 12
   */
  borderRadius?: number | string;

  /**
   * Custom padding
   * @default 2 (theme spacing units)
   */
  padding?: number | string;

  /**
   * Enable ambient breathing glow pulse.
   * @default false
   */
  breathing?: boolean;

  /**
   * Enable neon sign flicker effect.
   * @default false
   */
  flicker?: boolean;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Children
   */
  children?: React.ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledNeonCardProps {
  $isDark: boolean;
  $accent: NeonAccent;
  $glowOnHover: boolean;
  $glowIntensity: NeonGlowIntensity;
  $hoverLift: boolean;
  $borderRadius: number | string;
  $padding: number | string;
  $clickable: boolean;
  $breathing: boolean;
  $flicker: boolean;
}

const StyledNeonCard = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledNeonCardProps>(
  ({
    theme,
    $isDark,
    $accent,
    $glowOnHover,
    $glowIntensity,
    $hoverLift,
    $borderRadius,
    $padding,
    $clickable,
    $breathing,
    $flicker,
  }) => {
    const styles = getNeonStyles($accent, $isDark);

    // Calculate responsive padding (reduce by 0.5 spacing unit on mobile)
    const basePadding = typeof $padding === 'number' ? $padding : 2;
    const mobilePadding = Math.max(basePadding - 0.5, 1); // Min 1 spacing unit

    return {
      position: 'relative',
      borderRadius: typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius,
      padding: typeof $padding === 'number' ? theme.spacing($padding) : $padding,
      overflow: 'hidden',
      transition: transitionPresets.card,
      cursor: $clickable ? 'pointer' : 'default',

      // Responsive padding for mobile
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(mobilePadding),
      },

      // Liquid glass base with accent tint
      background: $isDark ? 'rgba(28, 28, 30, 0.42)' : 'rgba(255, 255, 255, 0.50)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      border: `0.5px solid ${alpha(styles.border, 0.5)}`,
      boxShadow: $glowOnHover
        ? `inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.10' : '0.5'})`
        : `${createNeonGlow($accent, $glowIntensity)}, inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.10' : '0.5'})`,

      // Hover effects — softened glow (~40% less)
      '&:hover': {
        background: $isDark ? 'rgba(28, 28, 30, 0.52)' : 'rgba(255, 255, 255, 0.62)',
        boxShadow: $glowOnHover
          ? `${createNeonGlow($accent, $glowIntensity)}, inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.12' : '0.55'})`
          : undefined,
        ...($hoverLift && {
          transform: 'scale(1.01)',
        }),
      },

      // Active state
      '&:active': {
        ...($clickable && {
          transform: 'translateY(-2px)',
        }),
      },

      // Breathing glow animation — reduced intensity
      ...($breathing && {
        animation: 'neonBreathing 4s ease-in-out infinite',
        '@keyframes neonBreathing': {
          '0%, 100%': {
            boxShadow: `${createNeonGlow($accent, 'subtle')}, inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.10' : '0.5'})`,
          },
          '50%': {
            boxShadow: `${createNeonGlow($accent, 'subtle')}, inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.14' : '0.55'})`,
          },
        },
      }),

      // Neon flicker effect
      ...($flicker && {
        animation: 'neonFlicker 4s infinite',
        '@keyframes neonFlicker': {
          '0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100%': { opacity: 1 },
          '20%, 21.999%, 63%, 63.999%, 65%, 69.999%': { opacity: 0.6 },
        },
      }),
    };
  },
);

// ============================================================================
// Component
// ============================================================================

export const NeonCard = forwardRef<HTMLDivElement, NeonCardProps>(
  (
    {
      children,
      accent = 'cyan',
      glowOnHover = true,
      glowIntensity = 'subtle',
      hoverLift = true,
      animate = false,
      animationDelay = 0,
      borderRadius = 12,
      padding = 2,
      breathing = false,
      flicker = false,
      onClick,
      sx,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Animation variants
    const animationProps: HTMLMotionProps<'div'> = animate
      ? {
          initial: motionPresets.fadeInUp.initial,
          animate: motionPresets.fadeInUp.animate,
          transition: {
            ...motionPresets.fadeInUp.transition,
            delay: animationDelay,
          },
        }
      : {};

    // Hover animation (when not using CSS hover for lift)
    const hoverProps = hoverLift
      ? {
          whileHover: interactionAnimations.cardHover,
        }
      : {};

    return (
      <Box sx={sx} {...props}>
        <StyledNeonCard
          ref={ref}
          $isDark={isDark}
          $accent={accent}
          $glowOnHover={glowOnHover}
          $glowIntensity={glowIntensity}
          $hoverLift={false} // We use framer-motion for hover instead
          $borderRadius={borderRadius}
          $padding={padding}
          $clickable={!!onClick}
          $breathing={breathing}
          $flicker={flicker}
          onClick={onClick}
          {...animationProps}
          {...hoverProps}
        >
          {children}
        </StyledNeonCard>
      </Box>
    );
  },
);

NeonCard.displayName = 'NeonCard';

export default NeonCard;
